// Timeline presets: they only generate keyframes, so everything stays editable.

import { CAMERA_TRACK, type Key, type SceneDesc, type Vec3 } from './types';

export type PresetKind = 'spin' | 'jump' | 'pulse' | 'wave' | 'flythrough';

function setTrack(scene: SceneDesc, target: string, keys: Key[]) {
  scene.timeline.tracks = scene.timeline.tracks.filter((t) => t.target !== target);
  scene.timeline.tracks.push({ target, keys });
}

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];

/** Applies a preset to node `id` (or the camera for `flythrough`). Mutates `scene`. */
export function applyPreset(scene: SceneDesc, kind: PresetKind, id: string | null) {
  const d = scene.timeline.duration || 4;
  if (kind === 'flythrough') {
    const c = scene.camera;
    const dir: Vec3 = [c.target[0] - c.position[0], c.target[1] - c.position[1], c.target[2] - c.position[2]];
    const len = Math.hypot(...dir) || 1;
    const step = Math.min(len * 0.25, (c.far - c.near) * 0.5);
    const fwd: Vec3 = [(dir[0] / len) * step, (dir[1] / len) * step, (dir[2] / len) * step];
    setTrack(scene, CAMERA_TRACK, [
      { time: 0, position: c.position, target: c.target, easing: 'ease_in_out' },
      { time: d / 2, position: add(add(c.position, fwd), [step * 0.4, 0, 0]), target: c.target, easing: 'ease_in_out' },
      { time: d, position: c.position, target: c.target, easing: 'linear' },
    ]);
    return;
  }
  const node = scene.nodes.find((n) => n.id === id);
  if (!node) return;
  const t = node.transform;
  switch (kind) {
    case 'spin':
      setTrack(scene, node.id, [
        { time: 0, rotation: t.rotation, easing: 'linear' },
        { time: d, rotation: add(t.rotation, [0, 360, 0]), easing: 'linear' },
      ]);
      break;
    case 'jump': {
      const up: Vec3 = add(t.position, [0, 0, 1.2]);
      setTrack(scene, node.id, [
        { time: 0, position: t.position, easing: 'ease_out' },
        { time: d / 2, position: up, easing: 'ease_in' },
        { time: d, position: t.position, easing: 'linear' },
      ]);
      break;
    }
    case 'pulse': {
      const big: Vec3 = [t.scale[0] * 1.25, t.scale[1] * 1.25, t.scale[2] * 1.25];
      setTrack(scene, node.id, [
        { time: 0, scale: t.scale, easing: 'ease_in_out' },
        { time: d / 2, scale: big, easing: 'ease_in_out' },
        { time: d, scale: t.scale, easing: 'linear' },
      ]);
      break;
    }
    case 'wave': {
      const children = scene.nodes.filter((n) => n.parent === node.id);
      if (children.length) {
        // Children bob along Z with staggered phases: a wave runs through the group.
        children.forEach((ch, i) => {
          const phase = i / children.length;
          const keys: Key[] = [];
          for (let k = 0; k <= 8; k++) {
            const time = (k / 8) * d;
            const z = 0.6 * Math.sin(2 * Math.PI * (k / 8 - phase));
            keys.push({ time, position: add(ch.transform.position, [0, 0, z]), easing: 'ease_in_out' });
          }
          setTrack(scene, ch.id, keys);
        });
      } else {
        setTrack(scene, node.id, [
          { time: 0, rotation: t.rotation, easing: 'ease_in_out' },
          { time: d / 4, rotation: add(t.rotation, [15, 25, 0]), easing: 'ease_in_out' },
          { time: (3 * d) / 4, rotation: add(t.rotation, [-15, -25, 0]), easing: 'ease_in_out' },
          { time: d, rotation: t.rotation, easing: 'linear' },
        ]);
      }
      break;
    }
  }
}
